import { useState } from 'react';

import { CheckCircle2, GraduationCap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';

import { useGuideStatus } from '../hooks/use-guide-status';
import { GuidePopover } from './guide-popover';

interface GuideBannerProps {
  isCollapsed: boolean;
}

export function GuideBanner({ isCollapsed }: GuideBannerProps) {
  const { t } = useTranslation('authenticated.onboarding-checklist');
  const { data: status } = useGuideStatus();
  const [open, setOpen] = useState(false);

  if (!status?.visible) {
    return null;
  }

  const completedCount = status.items.filter(item => item.completed).length;
  const totalCount = status.items.length;
  const allCompleted = completedCount === totalCount;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton tooltip={`${completedCount}/${totalCount} ${t('fab.label')}`}>
              <div className="relative">
                {allCompleted ? (
                  <CheckCircle2 className="size-4 text-green-600" />
                ) : (
                  <>
                    <GraduationCap className="size-4" />
                    <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                      {completedCount}/{totalCount}
                    </span>
                  </>
                )}
              </div>
              <span>{t('fab.label')}</span>
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" sideOffset={8} className="w-[380px] p-0">
            <GuidePopover items={status.items} onClosePopover={() => setOpen(false)} />
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="flex w-full flex-col gap-2 rounded-md border bg-card px-3 py-2.5 text-left transition-colors hover:bg-accent">
            <div className="flex items-center gap-2">
              {allCompleted ? (
                <CheckCircle2 className="size-4 shrink-0 text-green-600" />
              ) : (
                <GraduationCap className="size-4 shrink-0" />
              )}
              <span className="flex-1 text-sm font-medium">{t('fab.label')}</span>
              <span className="text-xs text-muted-foreground">
                {completedCount}/{totalCount}
              </span>
            </div>
            <Progress
              value={progressPercent}
              className="h-1.5"
              indicatorColor={
                allCompleted ? 'hsl(var(--success, 142 71% 45%))' : 'hsl(var(--primary))'
              }
            />
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" sideOffset={8} className="w-[380px] p-0">
          <GuidePopover items={status.items} onClosePopover={() => setOpen(false)} />
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  );
}
